// src/components/ChatBubbleFrontend.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './ChatBubbleFrontend.css';

/* ──── Activos ──── */
import backgroundImage      from '../assets/background-bubble.png';
import defaultBubbleImage   from '../assets/Burbuja-chat-predeterminada-violeta.png';
import PaperPlane           from '../assets/paper-clip-button.png';
import theThingLogo         from '../assets/Burbuja-chat-predeterminada-violeta.png';
import WhatsAppIcon         from '../assets/WhatsApp_icon.png';
import VoiceMessagePlayer   from './VoiceMessagePlayer';

/* ──── FontAwesome ──── */
import { FontAwesomeIcon }  from '@fortawesome/react-fontawesome';
import { faComments }       from '@fortawesome/free-solid-svg-icons';

/* ──── Firebase ──── */
import {
  arrayUnion,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

/* ══════════════════════════════════════════════════════════════
   COMPONENTE
   ══════════════════════════════════════════════════════════════ */
export const ChatBubbleFrontend = ({
  /*   Opcionales si algún día los pasas como prop:   */
  customStyles         = {},
  showChatIconButton   = true,
}) => {

  /* ──── 1) Parámetros y estado ──── */
  const { assistantId } = useParams();               // <- se mantiene igual
  const [isOpen,       setIsOpen]        = useState(false);
  const [isChatOpen,   setIsChatOpen]    = useState(false);
  const [hideButtons,  setHideButtons]   = useState(false);

  const [messages,     setMessages]      = useState([]);
  const [inputMessage, setInputMessage]  = useState('');
  const [assistantDetails, setAssistantDetails] = useState(null);
  const [isLoading,    setIsLoading]     = useState(false);
  const [threadId,     setThreadId]      = useState(null);
  const [error,        setError]         = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(true);

  const messagesEndRef = useRef(null);

  /* ──── 2) Estilos personalizados (igual que ChatBubble) ──── */
  const {
    headerColor              = 'linear-gradient(170deg, #6268d4, #7400dc)',
    bubbleButtonColor        = '#FFFFFF',
    messageUserColor         = 'linear-gradient(180deg, rgba(149, 59, 245, .179), #57238fd9)',
    messageAssistantColor    = 'linear-gradient(180deg,#40bfd563,#406ad563,#6740d563)',
    bubbleButtonImage        = defaultBubbleImage,
    useBackgroundImage       = true,
    backgroundColor          = '#FFFFFF',
    includeWaves             = true,
    chatIconButtonColor,
    chatIconColor            = '#ffffff',
    messageContentColor      = '#FFFFFF',
  } = customStyles;

  const hasWhatsApp = Boolean(assistantDetails && assistantDetails.phone_number);
  const isShowChatIcon = showChatIconButton ?? true;

  function isColorLight(hex) {
    const c = hex.replace('#', '');
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 186;
  }
  const isLightBackground = useBackgroundImage ? false : isColorLight(backgroundColor);
  const inputClassName = `chat-input-bubble-frontend ${isLightBackground ? 'light-background' : 'dark-background'}`;
  const spinnerColor = headerColor === 'linear-gradient(170deg, #6268d4, #7400dc)' ? '#693DD7' : headerColor;

  /* ──── 3) Cargar detalles del asistente (Firestore) ──── */
  useEffect(() => {
    const fetchAssistantDetails = async () => {
      if (!assistantId) { setIsFetchingDetails(false); return; }
      try {
        const ref = doc(db, 'assistants', assistantId);
        const snap = await getDoc(ref);
        setAssistantDetails(snap.exists() ? snap.data() : null);
      } catch (err) {
        console.error(err);
        setAssistantDetails(null);
      } finally {
        setIsFetchingDetails(false);
      }
    };
    fetchAssistantDetails();
  }, [assistantId]);

  /* ──── 4) Suscripción tiempo-real a Firestore para el hilo ──── */
 /* ──── 4) Suscripción tiempo-real a Firestore para el hilo ──── */
useEffect(() => {
  if (!assistantId || !threadId) return;

  const unsub = onSnapshot(
    doc(db, `threads_${assistantId}`, threadId),
    (snap) => {
      if (!snap.exists()) return;

      const data = snap.data();
      const hist = data.messageHistory || [];

      /* ---------- 1) deduplicar ---------- */
      const seen = new Set();
      const clean = [];
      for (const m of hist) {
        const key = `${m.role}|${m.content}|${m.createdAt}`;
        if (!seen.has(key)) {
          seen.add(key);
          clean.push(m);
        }
      }

      /* ---------- 2) ordenar: texto primero, adjunto después ---------- */
      const p = { text: 0, image: 1, pdf: 1, office: 1, video: 1, audio: 1 };
      clean.sort((a, b) => {
        if (a.createdAt === b.createdAt) {
          return (p[a.messageType || 'text'] - p[b.messageType || 'text']);
        }
        return a.createdAt - b.createdAt;
      });

      /* ---------- 3) mapear al formato de UI ---------- */
      const mapped = clean.map((m) => {
        let type = m.messageType || 'text';
        if (!m.messageType && typeof m.content === 'string') {
          const url = m.content.toLowerCase();
          if (url.match(/\.(png|jpe?g|gif|webp)$/))                     type = 'image';
          else if (url.endsWith('.pdf'))                               type = 'pdf';
          else if (url.match(/\.(xls|xlsx|doc|docx|ppt|pptx)$/))       type = 'office';
          else if (url.match(/\.(mp4|webm|ogg|mov)$/))                 type = 'video';
          else if (url.match(/\.(mp3|ogg|wav|m4a|aac)$/))              type = 'audio';
        }
        return {
          sender      : m.role === 'user' ? 'user' : 'assistant',
          text        : m.content,
          createdAt   : m.createdAt,
          messageType : type,
          caption     : m.caption || '',
        };
      });

      setMessages(mapped);
    },
    (err) => console.error('onSnapshot error:', err),
  );

  return () => unsub();
}, [assistantId, threadId]);

  /* ──── 5) Keep-alive opcional ──── */
  useEffect(() => {
    if (!assistantId || !threadId) return;
    const ref = doc(db, `threads_${assistantId}`, threadId);
    const timer = setInterval(() => {
      updateDoc(ref, { lastPing: Date.now(), isOnline: true }).catch(console.error);
    }, 15000);
    return () => clearInterval(timer);
  }, [assistantId, threadId]);

  /* ──── 6) Scroll al último mensaje ──── */
  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); };
  useEffect(scrollToBottom, [messages]);

  /* ──── 7) Formateo de contenido texto ──── */
  function formatMessage(content) {
    let out = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/###\s*(.*?)(\n|$)/g, '<h3>$1</h3>')
      .replace(/(https?:\/\/[^\s)]+)([.,;:)\?!]+)?/g,
        (_, url, p2) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="chat-link">${url}</a>${p2 || ''}`);
    out = out.replace(/\n/g, '<br>');
    return out;
  }

/* ──── 8) Envío de mensaje ──── */
const handleSendMessage = async (e) => {
  e.preventDefault();
  if (!assistantId || inputMessage.trim() === '') return;

  const clientTs = Date.now();

  /* 1 · burbuja optimista del usuario */
  const optimistic = [
    ...messages,
    { text: inputMessage, sender: 'user', createdAt: clientTs, messageType: 'text' }
  ];
  setMessages(optimistic);
  setInputMessage('');
  setIsLoading(true);

  /* 2 · guarda optimista en Firestore si ya existe el thread */
  if (threadId) {
    updateDoc(doc(db, `threads_${assistantId}`, threadId), {
      messageHistory: arrayUnion({
        role: 'user',
        content: inputMessage,
        createdAt: clientTs, 
        messageType: 'text'
      }),
    }).catch(console.error);
  }

  try {
    /* 3 · llamada al proxy */
    const res = await fetch('https://backend.thethingapp.com/api/chat-proxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assistantId, message: inputMessage, threadId, skipAttachmentsInHistory: true, channel: 'preview' }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Error al procesar mensaje');

    const data           = await res.json();
    const assistantMsg   = data.reply || 'No se recibió respuesta.';
    const attachments    = data.attachments || [];   // [{ url, type, caption? }]
    const receivedThread = data.threadId;
    const finalThreadId  = receivedThread || threadId;

    /* ───────────── FIX DEDUP ───────────── */
    const ts = Date.now(); // ← misma marca de tiempo p/ todo
    /* ───────────────────────────────────── */

    const assistantBubble = {
      role       : 'assistant',
      content    : assistantMsg,
      createdAt  : ts,
      messageType: 'text',
    };

    const attBubblesFS = attachments.map((a, idx) => ({
      role       : 'assistant',
      content    : a.url,
      createdAt  : ts + idx + 1, // mismo “origen”, pero único por adjunto
      messageType: a.type,
      caption    : a.caption || '',
    }));

    /* los reutilizo para el UI ─ así comparten exactamente los mismos datos */
    const attBubblesUI = attBubblesFS.map(b => ({
      text       : b.content,
      sender     : 'assistant',
      createdAt  : b.createdAt,
      messageType: b.messageType,
      caption    : b.caption,
    }));

  const nowIso = new Date().toISOString();

    /* 5 · crear o actualizar el documento del thread */
    if (!threadId && receivedThread) {
      await setDoc(
        doc(db, `threads_${assistantId}`, receivedThread),
        {
              createdAt     : nowIso,          //  ← NUEVO
    lastMessageAt : nowIso,          //  (útil para futuros ordenamientos)
          messageHistory: [
            { role: 'user', content: inputMessage, createdAt: clientTs, messageType: 'text' },
            assistantBubble,
            ...attBubblesFS
          ],
          lastPing : Date.now(),
          isOnline : true,
        },
        { merge: true }
      );
      setThreadId(receivedThread);
    } else {
      await updateDoc(
        doc(db, `threads_${assistantId}`, finalThreadId),
        {
          lastMessageAt: new Date().toISOString(),
          messageHistory: arrayUnion(
            assistantBubble,
            ...attBubblesFS
          ),
        }
      );
    }

    /* 6 · reflejar TODO en la UI: sólo texto (las imágenes entrarán vía onSnapshot) */
    setMessages([
      ...optimistic,
      { text: assistantMsg, sender: 'assistant', createdAt: ts, messageType: 'text' }
    ]);

  } catch (err) {
    console.error(err);
    setMessages([
      ...optimistic,
      { text: 'Hubo un error al procesar tu mensaje.', sender: 'assistant', messageType: 'text' }
    ]);
  } finally {
    setIsLoading(false);
  }
};



  /* ──── 9) Guardar hilo (opcional) ──── */
  const saveChatThread = async (aid, tid, msgs) => {
    try {
      await fetch('https://backend.thethingapp.com/api/save-web-chat-thread', {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({
          assistantId: aid,
          threadId  : tid,
          messages  : msgs.map(m => ({
            role       : m.sender === 'user' ? 'user' : 'assistant',
            content    : m.text,
            createdAt  : m.createdAt,
            messageType: m.messageType || 'text',
            caption    : m.caption || '',
          })),
        }),
      });
    } catch (err) { console.error('saveChatThread', err); }
  };

  /* ──── 10) Apertura / cierre de burbuja ──── */
  const toggleChat = () => {
    if (!isOpen) { setIsOpen(true); }
    else {
      setHideButtons(true);
      setTimeout(() => { setIsOpen(false); setHideButtons(false); }, 500);
    }
  };
  const openChatWindow = () => {
    setHideButtons(true);
    setTimeout(() => { setIsChatOpen(true); setIsOpen(false); setHideButtons(false); }, 500);
  };
  const closeChatWindow = () => { setIsChatOpen(false); setIsOpen(false); };

  /* ──── 11) Loading inicial ──── */
  if (error) return <div className="chat-bubble-container">Error: {error}</div>;
  if (isFetchingDetails) {
    return (
      <div id="chat-bubble-container" className="chat-bubble-container">
        <div className="spinner-bubble-bundle" style={{ borderTopColor: spinnerColor }}/>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════
     12) RENDER UI  — TODO a partir de aquí es IDENTICO
     ════════════════════════════════════════════════════ */
  return (
    <div id="chat-bubble-container" className="chat-bubble-container">

      {/* ────── Botón principal (burbuja grande) ────── */}
      <div id="chat-buttons-wrapper" className="chat-buttons-wrapper">
        {!isChatOpen && (
          <button
            onClick={toggleChat}
            className="chat-bubble-button-frontend"
            id="chat-bubble-button-frontend"
            aria-label="Abrir opciones de chat"
            style={{ background: bubbleButtonColor === '#FFFFFF' ? 'transparent' : bubbleButtonColor }}
          >
            {includeWaves && (
              <>
                <span className="wave wave1"></span>
                <span className="wave wave2"></span>
                <span className="wave wave3"></span>
                <span className="wave wave4"></span>
                <span className="wave wave5"></span>
              </>
            )}
            <img src={bubbleButtonImage} alt="Chat" className="chat-bubble-image-frontend" />
          </button>
        )}
      </div>

      {/* ────── Botones secundarios (chat web / WhatsApp) ────── */}
      {isOpen && !isChatOpen && (
        <div id="buttons-container" className="buttons-container">
          {isShowChatIcon && (
            <button
              id="chat-icon-button"
              className={`chat-icon-button ${hideButtons ? 'fade-out-right' : 'fade-in-right'} ${hasWhatsApp ? 'hasWhatsApp' : 'noWhatsApp'}`}
              onClick={(e) => { e.stopPropagation(); openChatWindow(); }}
              aria-label="Abrir chat"
              style={{ backgroundColor: chatIconButtonColor || '#693DD7' }}
            >
              <FontAwesomeIcon icon={faComments} className="small-icon" style={{ color: chatIconColor, width:'2.5em' }} />
            </button>
          )}
          {assistantDetails?.phone_number && (
            <button
              id="whatsapp-bubble-large"
              className={`whatsapp-bubble-large ${hideButtons ? 'fade-out-right' : 'fade-in-right'}`}
              onClick={(e) => {
                e.stopPropagation();
                const phone = assistantDetails.phone_number.replace(/\D/g, '');
                window.open(`https://wa.me/${phone}`, '_blank');
              }}
              aria-label="Abrir chat de WhatsApp"
            >
              <img src={WhatsAppIcon} alt="WhatsApp" className="whatsapp-icon-large" />
            </button>
          )}
        </div>
      )}

      {/* ────── Ventana del chat web ────── */}
      {isChatOpen && (
        <div
          className="chat-window-frontend fade-in"
          style={{
            backgroundImage : useBackgroundImage ? `url(${backgroundImage})` : 'none',
            backgroundColor : useBackgroundImage ? 'transparent' : backgroundColor,
            backgroundSize  : 'cover',
            backgroundPosition: 'center',
            backgroundRepeat  : 'no-repeat',
          }}
        >
          {/* Encabezado */}
          <div className="chat-header-bubble" style={{ background: headerColor }}>
            <h3 className="h3-bubble-frontend">
              {assistantDetails?.name || 'Chat'}
            </h3>
            <button onClick={closeChatWindow} className="close-button-bubble" aria-label="Cerrar chat">x</button>
          </div>

          {/* Lista de mensajes */}
          <div className="chat-messages-bubble-frontend">
            {messages.map((msg, i) => {
              const isMedia = ['image','pdf','office','video','audio'].includes(msg.messageType);
              const style = {
                background: isMedia ? 'transparent' : (msg.sender === 'user' ? messageUserColor : messageAssistantColor),
                color: messageContentColor,
                boxShadow: isMedia ? 'none' : '0 1px 4px rgba(0,0,0,0.1)',
                padding: isMedia ? '0' : '1em 1.3em',
              };
              return (
                <div key={i} className={`message ${msg.sender} ${msg.messageType}`} style={style}>
                                  {/* TEXTO */}
                  {(!msg.messageType || msg.messageType === 'text') && (
                    <span className="message-content-bubble-frontend" style={{ color: messageContentColor }} dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }} />
                  )}
                  {/* IMAGEN */}
                  {msg.messageType === 'image' && (
                    <>
                      <img src={msg.text} alt="Imagen enviada" className="chat-image" style={{ maxWidth:'250px', borderRadius:'8px' }} />
                      {msg.caption && (
                        <div className="message-caption" style={{ marginTop:'4px' }} dangerouslySetInnerHTML={{ __html: formatMessage(msg.caption) }}/>
                      )}
                    </>
                  )}
                  {/* PDF */}
                  {msg.messageType === 'pdf' && (
                    <div className="message-training-file">
                      <embed src={msg.text} type="application/pdf" width="300" height="500" />
                    </div>
                  )}
                  {/* OFFICE */}
                  {msg.messageType === 'office' && (
                    <div className="message-office-preview">
                      <iframe
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(msg.text)}`}
                        width="300" height="200" frameBorder="0" title="Office document preview"
                      />
                    </div>
                  )}
                  {/* VÍDEO */}
                  {msg.messageType === 'video' && (
                    <video controls style={{ maxWidth:'250px', borderRadius:'8px' }}>
                      <source src={msg.text} />
                      Tu navegador no soporta el tag de video.
                    </video>
                  )}
                  {/* AUDIO */}
                  {msg.messageType === 'audio' && (
                    <VoiceMessagePlayer src={msg.text} />
                  )}
  
                </div>
              );
            })}

            {/* Loading… */}
            {isLoading && (
              <div className="message assistant loading-container">
                <div className="loading-dots"><span className="dot"/><span className="dot"/><span className="dot"/></div>
              </div>
            )}
            <div ref={messagesEndRef}/>
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="chat-input-container-bubble-frontend">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Escribe tu mensaje..."
              className={inputClassName}
              disabled={isLoading}
            />
            <button type="submit" className="send-button-bubble-frontend" disabled={isLoading}>
              <img src={PaperPlane} alt="Send-Button" className="send-button-img-bubble-frontend" />
            </button>
          </form>

          {/* Watermark */}
          <div className={`watermark ${isLightBackground ? 'light-background' : 'dark-background'}`}>
            <span className="powered-by">Desarrollado por </span>
            <a href="https://thethingapp.com/" target="_blank" rel="noopener noreferrer" className="watermark-link">
              <img src={theThingLogo} alt="The Thing" className="watermark-logo" />
              The Thing
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBubbleFrontend;
